// CC-19 — Bloc Prestation AMO depuis catalogue
// Dropdown lié à metier_catalogue_services (categorie=prestations, actif=true).
// Auto-fill montant_amo_ht et montant_amo (TTC) côté backend après PATCH.
import { useEffect, useState } from "react";
import { Edit3, Save, X, Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { API, getAuthHeaders } from "./helpers";
import { toast } from "sonner";

const fmtEuro = (n) =>
  n != null
    ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n)
    : "—";

export function PrestationAMOBlock({ dossier, dossierId, tenantId, verrouille, onUpdate }) {
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(dossier?.prestation_amo_ref || "");

  useEffect(() => {
    let cancelled = false;
    const loadServices = async () => {
      setLoadingServices(true);
      try {
        const res = await fetch(
          `${API}/api/metier/catalogue/services?categorie=prestations&actif_only=true`,
          { headers: getAuthHeaders(tenantId), credentials: "include" }
        );
        if (res.ok && !cancelled) {
          const data = await res.json();
          // L'endpoint retourne directement la liste
          setServices(Array.isArray(data) ? data : []);
        }
      } catch {
        /* silencieux : la dropdown affichera "aucune prestation" */
      } finally {
        if (!cancelled) setLoadingServices(false);
      }
    };
    if (editing) loadServices();
    return () => {
      cancelled = true;
    };
  }, [editing, tenantId]);

  const startEdit = () => {
    setSelected(dossier?.prestation_amo_ref || "");
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setSelected(dossier?.prestation_amo_ref || "");
  };

  const save = async () => {
    if (!selected) {
      toast.error("Sélectionnez une prestation");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}`, {
        method: "PATCH",
        headers: getAuthHeaders(tenantId, "application/json"),
        credentials: "include",
        body: JSON.stringify({ prestation_amo_ref: selected }),
      });
      if (res.ok) {
        toast.success("Prestation AMO mise à jour");
        setEditing(false);
        onUpdate?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur sauvegarde");
      }
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setSaving(false);
    }
  };

  const designation = dossier?.prestation_amo_designation;
  const ref = dossier?.prestation_amo_ref;
  const ht = dossier?.montant_amo_ht;
  const ttc = dossier?.montant_amo;

  return (
    // Pas d'accordéon interne : CommercialSection fournit déjà la carte
    // « Prestation AMO » + pastille. Ici = contenu compact uniquement (mockup).
    <div data-testid="bloc-prestation-amo">
          <div className="flex justify-end mb-3">
            {!verrouille &&
              (editing ? (
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={save}
                    disabled={saving}
                    className="h-7 px-2"
                    data-testid="save-prestation-amo-btn"
                    aria-label="Enregistrer la prestation"
                  >
                    {saving ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--oh-success-text)]" />
                    ) : (
                      <Save className="w-3.5 h-3.5 text-[var(--oh-success-text)]" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={cancelEdit}
                    className="h-7 px-2"
                    aria-label="Annuler"
                  >
                    <X className="w-3.5 h-3.5 text-[var(--oh-error-text)]" />
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={startEdit}
                  className="h-7 px-2"
                  data-testid="edit-prestation-amo-btn"
                  aria-label="Modifier la prestation"
                >
                  <Edit3
                    className="w-3.5 h-3.5"
                    style={{ color: "var(--oh-text-muted)" }}
                  />
                </Button>
              ))}
          </div>

          {editing ? (
            <div className="space-y-3">
              <div>
                <label
                  className="text-[10px] uppercase tracking-wide mb-1 block"
                  style={{ color: "var(--oh-text-muted)" }}
                >
                  Prestation (depuis le catalogue)
                </label>
                <select
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                  disabled={loadingServices}
                  className="w-full px-3 py-2 rounded-lg text-sm border"
                  style={{
                    background: "var(--oh-bg-surface)",
                    borderColor: "var(--oh-border)",
                    color: "var(--oh-text)",
                  }}
                  data-testid="select-prestation-amo"
                >
                  <option value="">— Sélectionner —</option>
                  {services.map((s) => (
                    <option key={s.reference} value={s.reference}>
                      {s.designation} ({fmtEuro(s.prix_ht)} HT)
                    </option>
                  ))}
                </select>
                {loadingServices && (
                  <p className="text-xs mt-1" style={{ color: "var(--oh-text-muted)" }}>
                    Chargement du catalogue…
                  </p>
                )}
              </div>
              <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
                Le HT et le TTC sont remplis automatiquement depuis la prestation
                choisie (TVA 10% par défaut). Pour ajouter une prestation, passer
                par <strong>Catalogue → Services</strong>.
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="text-sm flex items-center gap-2 flex-wrap">
                <span style={{ color: "var(--oh-text-muted)" }}>Prestation :</span>
                <span className="font-medium" data-testid="prestation-amo-label" style={{ color: "var(--oh-text)" }}>{designation || ref || "Non définie"}</span>
                {ref && designation && <span className="text-[11px] font-mono" style={{ color: "var(--oh-text-muted)" }}>({ref})</span>}
              </div>
              <div className="text-sm flex items-center gap-2 flex-wrap">
                <span style={{ color: "var(--oh-text-muted)" }}>Montant :</span>
                <span className="font-medium" data-testid="prestation-amo-ht" style={{ color: "var(--oh-text)" }}>{fmtEuro(ht)} HT</span>
                <span style={{ color: "var(--oh-text-muted)" }}>·</span>
                <span className="font-medium" data-testid="prestation-amo-ttc" style={{ color: "var(--oh-hub-finance)" }}>{fmtEuro(ttc)} TTC</span>
              </div>
            </div>
          )}
    </div>
  );
}
